import { LegalHeading, LegalPageFrame, LegalParagraph } from '@/components/legal/legal-document';
import { faqJsonLd } from '@/components/seo/faq-list';
import { SemanticTable } from '@/components/seo/semantic-table';
import { SeoHead } from '@/components/seo-head';
import {
  exampleNetAmounts,
  HELP_EXAMPLE_AMOUNT,
  PAYMENT_HELP_ARTICLES,
} from '@/content/payments/help-articles';
import { FALLBACK_PUBLIC_PRICING } from '@/content/payments/public-pricing';
import { formatFeeSummary } from '@/content/payments/shared';
import { usePublicPaymentPricing } from '@/hooks/use-public-payment-pricing';
import { pageTitle } from '@/lib/seo/meta';
import { formatCurrency } from '@/utils/format-currency';
import React from 'react';
import { View } from 'react-native';

const HELP_TITLE = 'Taxas e prazos do recebimento pelo link';
const HELP_PATH = '/ajuda/recebimentos';
const HELP_DESCRIPTION =
  'Recebimento pelo link da Controlapag nos planos pagos: taxa do Pix, do cartão e do boleto, quando o dinheiro cai, limites, reembolso e seus dados.';

/**
 * Central "Receber pela Controlapag" (`/ajuda/recebimentos`). Pública, sem
 * login. O texto sai inteiro no HTML estático com a tabela de reserva de
 * `facts.ts` e troca pelos números vivos de
 * `GET /payment-account/pricing/public` quando a API responde. Cada artigo é
 * um H2 visível, e o `FAQPage` sai do mesmo array.
 */
export default function PaymentsHelpCenterScreen() {
  const { data } = usePublicPaymentPricing();
  const pricing = data ?? FALLBACK_PUBLIC_PRICING;
  const example = exampleNetAmounts(pricing);

  const articles = PAYMENT_HELP_ARTICLES.map((article) => ({
    slug: article.slug,
    question: article.title,
    answer: article.body(pricing),
  }));

  return (
    <LegalPageFrame
      screenTitle="Receber pela Controlapag"
      head={
        <SeoHead
          title={pageTitle(HELP_TITLE)}
          description={HELP_DESCRIPTION}
          path={HELP_PATH}
          jsonLd={[faqJsonLd(articles)]}
        />
      }
      title={HELP_TITLE}
      intro="O recebimento pelo link é um recurso dos planos pagos: o cliente paga com Pix, cartão ou boleto pelo link da cobrança, e ela dá baixa sozinha. A taxa é por pagamento."
      footerLinks={[
        { label: 'Falar com o suporte', href: '/suporte' },
        { label: 'Termos de uso', href: '/terms' },
      ]}
    >
      <View className="mb-10 gap-3">
        <LegalHeading>Taxas, exemplo e prazo de cada forma</LegalHeading>
        <SemanticTable
          caption={`Taxa por pagamento, quanto chega de uma cobrança de ${formatCurrency(HELP_EXAMPLE_AMOUNT)} e quando fica disponível`}
          columns={[
            { label: 'Forma' },
            { label: 'Taxa' },
            { label: `Chega de ${formatCurrency(HELP_EXAMPLE_AMOUNT)}` },
            { label: 'Disponível em' },
          ]}
          rows={[
            [
              'Pix',
              formatFeeSummary({ percent: pricing.fee.pixPercent, fixed: 0 }),
              formatCurrency(example.pix),
              `${pricing.settlement.pixBusinessDays} dias úteis`,
            ],
            [
              'Cartão',
              formatFeeSummary({ percent: pricing.fee.cardPercent, fixed: pricing.fee.cardFixed }),
              formatCurrency(example.card),
              `${pricing.settlement.cardDays} dias`,
            ],
            [
              'Boleto (se você ligar)',
              formatFeeSummary({ percent: 0, fixed: pricing.fee.boletoFixed }),
              formatCurrency(example.boleto),
              `${pricing.settlement.pixBusinessDays} dias úteis`,
            ],
          ]}
        />
      </View>

      {articles.map((article) => (
        <View key={article.slug} nativeID={article.slug} className="mb-8 gap-3">
          <LegalHeading>{article.question}</LegalHeading>
          <LegalParagraph>{article.answer}</LegalParagraph>
        </View>
      ))}
    </LegalPageFrame>
  );
}
